import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function FacilityOverview({ facility, onContactClick, onRequestInfo }) {
  // Handle undefined facility
  if (!facility) {
    return <div className="p-8 text-center text-gray-500">Facility information not available</div>;
  }
  
  const { 
    name, 
    address, 
    types = [], 
    description, 
    ratings = {}, 
    units = [], 
    organization
  } = facility;
  
  // Get lowest price unit
  const lowestPriceUnit = units && units.length > 0 
    ? units.reduce((lowest, unit) => 
        (!lowest || (unit.price && (lowest.price === undefined || unit.price < lowest.price))) ? unit : lowest, null)
    : null;
  
  // Format price
  const formatPrice = (price) => {
    if (price === undefined || price === null || isNaN(price)) {
      return 'Contact for pricing';
    }
    return `$${Math.round(price).toLocaleString()}`;
  };
  
  return (
    <div className="space-y-6">
      {/* Facility header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{name}</h1>
          <p className="text-lg text-gray-600 mt-1">{address}</p>
          
          {types.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {types.map((type, index) => (
                <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  {type}
                </Badge>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 lg:text-right">
          {lowestPriceUnit && lowestPriceUnit.price && !isNaN(lowestPriceUnit.price) ? (
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-700">Starting at</p>
              <p className="text-2xl font-bold text-blue-700">{formatPrice(lowestPriceUnit.price)}</p>
              <p className="text-xs text-blue-600">per month</p>
            </div>
          ) : null}
          
          <div className="flex flex-col gap-2">
            <Button className="bg-blue-600 hover:bg-blue-700">
              Schedule a Tour
            </Button>
            <Button 
              variant="outline" 
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={onContactClick}
            >
              Contact Community
            </Button>
          </div>
        </div>
      </div>
      
      {/* Ratings overview - only show if there are ratings */}
      {(ratings.food > 0 || ratings.care > 0 || ratings.cleanliness > 0) && (
        <Card className="overflow-hidden border-none shadow-md">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-3">Resident Feedback</h3>
                <div className="grid grid-cols-3 gap-4">
                  {ratings.food > 0 && (
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 text-blue-700 text-xl font-bold mb-1">
                        {ratings.food}
                      </div>
                      <p className="text-sm text-gray-600">Food</p>
                    </div>
                  )}
                  {ratings.care > 0 && (
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 text-blue-700 text-xl font-bold mb-1">
                        {ratings.care}
                      </div>
                      <p className="text-sm text-gray-600">Care</p>
                    </div>
                  )}
                  {ratings.cleanliness > 0 && (
                    <div className="text-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 text-blue-700 text-xl font-bold mb-1">
                        {ratings.cleanliness}
                      </div>
                      <p className="text-sm text-gray-600">Cleanliness</p>
                    </div>
                  )}
                </div>
              </div>
            
            <div className="border-t md:border-t-0 md:border-l border-gray-200 pt-4 md:pt-0 md:pl-6 flex-1">
              <h3 className="text-lg font-semibold mb-3">Available Units</h3>
              {units && units.length > 0 && units.filter(unit => unit.available).length > 0 ? (
                <div className="space-y-2">
                  {units.filter(unit => unit.available).slice(0, 2).map((unit, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{unit.room_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} - {unit.bathrooms} Bath</p>
                      </div>
                      <p className="font-semibold text-blue-700">{formatPrice(unit.price)}</p>
                    </div>
                  ))}
                  {units.filter(unit => unit.available).length > 2 && (
                    <p className="text-sm text-blue-600 mt-2">+ {units.filter(unit => unit.available).length - 2} more unit types available</p>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Contact for availability</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      )}
      
      {/* Tabs for details */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="w-full grid grid-cols-4 mb-6 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger 
            value="overview" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-medium data-[state=active]:text-blue-700 transition-all"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="units" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-medium data-[state=active]:text-blue-700 transition-all"
          >
            Units & Pricing
          </TabsTrigger>
          <TabsTrigger 
            value="amenities" 
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-medium data-[state=active]:text-blue-700 transition-all"
          >
            Amenities
          </TabsTrigger>
          {/* Only show Reviews tab if there are ratings */}
          {(ratings.food > 0 || ratings.care > 0 || ratings.cleanliness > 0) && (
            <TabsTrigger 
              value="reviews" 
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:font-medium data-[state=active]:text-blue-700 transition-all"
            >
              Reviews
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4">About {name}</h3>
              <div className="prose max-w-none">
                <p className="whitespace-pre-line text-gray-700">
                  {description || 'No description available.'}
                </p>
              </div>
              
              {organization && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-medium mb-2">About the Organization</h4>
                  <p className="text-gray-700">{organization.name}</p>
                  {organization.website && (
                    <a 
                      href={organization.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline mt-1 inline-block"
                    >
                      Visit Website
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="units" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-6">Units & Pricing</h3>
              
              {units && units.length > 0 ? (
                <div className="space-y-8">
                  {units.map((unit, index) => (
                    <div key={index} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
                        <h4 className="text-lg font-medium">
                          {unit.room_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} - {unit.bathrooms} Bath
                        </h4>
                        <div className="mt-2 md:mt-0">
                          <span className="text-2xl font-bold text-blue-600">{formatPrice(unit.price)}</span>
                          <span className="text-gray-600 ml-1">/month</span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <span className="text-gray-500 text-sm">Square Feet</span>
                          <p className="font-medium">{unit.square_feet ? `${unit.square_feet} sq ft` : 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-sm">Availability</span>
                          <p className="font-medium">
                            {unit.available 
                              ? 'Available' 
                              : 'Not Available'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 text-sm">Move-In</span>
                          <p className="font-medium">
                            {unit.available ? 'Available Now' : 'Contact for Details'}
                          </p>
                        </div>
                      </div>
                      
                      {unit.description && (
                        <p className="text-gray-700">{unit.description}</p>
                      )}
                      
                      {unit.available && (
                        <div className="mt-4">
                          <Button 
                            variant="outline" 
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            onClick={() => onRequestInfo({
                              roomType: unit.room_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
                              bathrooms: unit.bathrooms,
                              price: unit.price
                            })}
                          >
                            Request Information
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">No unit information available</p>
                  <Button>Contact for Pricing</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="amenities" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-6">Amenities</h3>
              {/* Amenities */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {facility.amenities && facility.amenities.length > 0 ? (
                  facility.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center">
                      <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{amenity.name}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 col-span-3">No amenities information available</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Only render reviews tab content if there are ratings */}
        {(ratings.food > 0 || ratings.care > 0 || ratings.cleanliness > 0) && (
          <TabsContent value="reviews" className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">Resident Reviews</h3>
                <Button variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                  Write a Review
                </Button>
              </div>
              
              <div className="flex flex-col md:flex-row gap-8 mb-8">
                <div className="flex flex-col items-center">
                  <div className="text-5xl font-bold text-blue-600 mb-1">4.8</div>
                  <div className="flex text-yellow-400 mb-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <svg key={star} className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">Based on 24 reviews</p>
                </div>
                
                <div className="flex-1 space-y-4">
                  <div className="flex items-center">
                    <span className="w-24 text-sm text-gray-600">Food</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full">
                      <div className="h-2 bg-blue-600 rounded-full" style={{ width: `${(ratings.food || 4.7) * 20}%` }}></div>
                    </div>
                    <span className="w-8 text-right text-sm font-medium ml-2">{ratings.food || 4.7}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 text-sm text-gray-600">Care</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full">
                      <div className="h-2 bg-blue-600 rounded-full" style={{ width: `${(ratings.care || 4.8) * 20}%` }}></div>
                    </div>
                    <span className="w-8 text-right text-sm font-medium ml-2">{ratings.care || 4.8}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="w-24 text-sm text-gray-600">Cleanliness</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full">
                      <div className="h-2 bg-blue-600 rounded-full" style={{ width: `${(ratings.cleanliness || 4.9) * 20}%` }}></div>
                    </div>
                    <span className="w-8 text-right text-sm font-medium ml-2">{ratings.cleanliness || 4.9}</span>
                  </div>
                </div>
              </div>
              
              {/* Reviews from backend */}
              <div className="space-y-6">
                {facility.reviews && facility.reviews.length > 0 ? (
                  facility.reviews.map((review, index) => {
                    // Format date
                    const reviewDate = new Date(review.created_at);
                    const now = new Date();
                    const diffMonths = (now.getFullYear() - reviewDate.getFullYear()) * 12 + 
                                       (now.getMonth() - reviewDate.getMonth());
                    const dateText = diffMonths < 1 ? 'Recently' : 
                                     diffMonths === 1 ? '1 month ago' : 
                                     `${diffMonths} months ago`;
                    
                    return (
                      <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-medium">{review.user_name || 'Anonymous'}</h4>
                            <p className="text-sm text-gray-500">{dateText}</p>
                          </div>
                          <div className="flex text-yellow-400">
                            {[1, 2, 3, 4, 5].map(star => (
                              <svg 
                                key={star} 
                                className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-400' : 'text-gray-300'}`} 
                                fill="currentColor" 
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-700">{review.content}</p>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500">No reviews available yet. Be the first to write a review!</p>
                )}
              </div>
              
              <div className="mt-6 text-center">
                <Button variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                  View All Reviews
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
